import type {Observable} from 'rxjs';
import {useCallback, useEffect, useState} from 'react';
import {useSyncExternalStore} from 'use-sync-external-store/shim';

import type {
  Client,
  Observability,
} from '@stripe-internal/conversations-client';
import {ViewMode} from '@stripe-internal/support-conversations-controller';

import type {AssistantState, ConversationLoadingOperation} from './types';
import type {ConversationOverview} from '../conversations/types';
import {DEFAULT_VIEW_MODE} from '../../core/types';
import {isConversationOverviewEnded} from '../conversations/conversationSelectors';
import type {ConversationViewModelState as LegacyConversationViewModelState} from '../viewModels/LegacyConversationViewModel';
import type {HistoryViewModelState as LegacyHistoryViewModelState} from '../viewModels/LegacyHistoryViewModel';
import {useConversationClientPending} from '../../core/hooks/useConversationClientPending';
import {useThreadsApp} from './ThreadsAppContext';

export {useThreadsApp} from './ThreadsAppContext';

export function useThreadsController() {
  const app = useThreadsApp();
  return {
    conversations: app.conversations,
    ui: {
      open: () => app.open(),
      close: (skipOnClose?: boolean) => app.close(skipOnClose),
      minimize: () => app.minimize(),
      openHistory: () => app.historyViewModel.open(),
      closeHistory: () => app.historyViewModel.close(),
      startNewConversation: () => app.startNewConversation(),
      requestAction: app.requestAction.bind(app),
      requestRestart: app.requestRestart.bind(app),
      confirmRestart: app.confirmRestart.bind(app),
      cancelRestart: app.cancelRestart.bind(app),
      confirmClose: app.confirmClose.bind(app),
      cancelClose: app.cancelClose.bind(app),
    },
    observability: app.observability,
  };
}

export function useThreadsObservability(): Observability {
  return useThreadsApp().observability;
}

export function useThreadsAnalytics() {
  return useThreadsApp().analytics;
}

export function useThreadsApi() {
  return useThreadsApp().api;
}

export function useThreadsConversationsController() {
  return useThreadsApp().conversations;
}

export function useThreadsConversationsClient(): Client | undefined {
  const app = useThreadsApp();
  const subscribe = useCallback(
    (listener: () => void) => {
      if (app.runtime === 'legacy') {
        return () => {
          // The legacy composite client stays constant for this app lifetime.
        };
      }
      const sub = app.conversations.selectionObservable().subscribe(listener);
      return () => sub.unsubscribe();
    },
    [app],
  );
  const getSnapshot = useCallback(
    () =>
      app.runtime === 'legacy'
        ? app.conversations.conversationsClient
        : app.conversations.selected?.client,
    [app],
  );
  return useSyncExternalStore(subscribe, getSnapshot);
}

export function useThreadsMessagePartClient() {
  const app = useThreadsApp();
  return app.runtime === 'legacy'
    ? app.conversations.messagePartClient
    : app.transports.uhClient;
}

export function useThreadsStateSelector() {
  const app = useThreadsApp();
  const {useSelector} = app.stateStore;
  return useSelector;
}

export function useSetThreadsState() {
  const app = useThreadsApp();
  return app.stateStore.setValue;
}

export function useThreadsSelector<T>(
  selector: (state: AssistantState) => T,
): T {
  const useSelector = useThreadsStateSelector();
  return useSelector(selector);
}

function useThreadsConversationsStateSelector() {
  const conversations = useThreadsConversationsController();
  const {useSelector} = conversations.stateStore;
  return useSelector;
}

export function useThreadsConfig() {
  return useThreadsSelector((state) => state.config);
}

export function useThreadsProfile() {
  return useThreadsSelector((state) => state.profile);
}

export function useThreadsVisitor() {
  return useThreadsSelector((state) => state.profile.visitor);
}

export function useThreadsFlags() {
  return useThreadsSelector((state) => state.profile.featureFlags);
}

export function useThreadsAudience() {
  return useThreadsSelector((state) => state.config.audience);
}

export function useThreadsEntryPoint() {
  return useThreadsSelector((state) => state.config.entryPoint);
}

export function useThreadsVisibility() {
  const app = useThreadsApp();
  const visibility = useThreadsSelector((state) => state.visibility);
  return {
    visibility,
    open: () => app.open(),
    minimize: () => app.minimize(),
    close: (skipOnClose?: boolean) => app.close(skipOnClose),
  };
}

export function useThreadsPendingConfirmation() {
  return useThreadsSelector((state) => state.pendingConfirmation);
}

export function useThreadsConfirmationActions() {
  const app = useThreadsApp();
  return {
    requestRestart: app.requestRestart.bind(app),
    confirmRestart: app.confirmRestart.bind(app),
    cancelRestart: app.cancelRestart.bind(app),
    confirmClose: app.confirmClose.bind(app),
    cancelClose: app.cancelClose.bind(app),
  };
}

export function useThreadsViewMode(): ViewMode {
  const stateViewMode = useThreadsSelector((state) => state.viewMode);
  return stateViewMode ?? DEFAULT_VIEW_MODE;
}

export function useThreadsLocale() {
  return useThreadsSelector((state) => state.config.locale);
}

export function useThreadsSTPBaseUrl() {
  return useThreadsSelector((state) => state.config.stripeThirdPartyBaseUrl);
}

export function useThreadsDashboardUrl() {
  return useThreadsSelector((state) => state.config.urls.dashboard);
}

export function useThreadsParentOrigin() {
  return useThreadsSelector((state) => state.config.parentOrigin);
}

export function useThreadsParentViewportSnapshot() {
  return useThreadsSelector((state) => state.parentViewportSnapshot);
}

export function useThreadsParentMessageBus() {
  return useThreadsApp().parentMessageBus;
}

export function useThreadsConversationsLoading() {
  const useSelector = useThreadsConversationsStateSelector();
  return useSelector((state) =>
    Object.values(state.loadingState).some(Boolean),
  );
}

export function useThreadsConversationViewModel() {
  const app = useThreadsApp();
  return app.conversationViewModel;
}

export function useThreadsHistoryViewModel() {
  const app = useThreadsApp();
  return app.historyViewModel;
}

export function useThreadsWithHistory(): boolean {
  return useThreadsSelector((state) => state.withHistory);
}

export function useThreadsIsLoading(
  loadingOperations: ConversationLoadingOperation[],
) {
  const useSelector = useThreadsConversationsStateSelector();
  return useSelector((state) =>
    loadingOperations.some((operation) => !!state.loadingState[operation]),
  );
}

export function useThreadsConversationViewModelState(): Omit<
  LegacyConversationViewModelState,
  'overview'
> & {overview?: ConversationOverview | null} {
  const viewModel = useThreadsConversationViewModel();
  const subscribe = useCallback(
    (listener: () => void) => {
      const state: Observable<unknown> = viewModel.stateObservable();
      const subscription = state.subscribe(listener);
      return () => subscription.unsubscribe();
    },
    [viewModel],
  );

  const getSnapshot = useCallback(() => {
    return viewModel.state;
  }, [viewModel]);

  const state = useSyncExternalStore(subscribe, getSnapshot);
  // Keep the shared UI contract while the legacy runtime is still supported.
  if ('loadStatus' in state) {
    const {loadStatus, ...selection} = state;
    return {
      ...selection,
      loading: loadStatus === 'loading',
      loadResult:
        loadStatus === 'success' || loadStatus === 'error' ? loadStatus : null,
    };
  }
  return state;
}

export function useThreadsCurrentConversationOverview(): ConversationOverview | null {
  const {id, conversation, loadResult} = useThreadsConversationViewModelState();
  const app = useThreadsApp();
  const subscribe = useCallback(
    (listener: () => void) => {
      const subscription =
        app.runtime === 'legacy'
          ? app.conversationViewModel.stateObservable().subscribe(listener)
          : app.conversations
              .overviewChangesObservable()
              .subscribe((overview) => {
                if (overview.id === id) {
                  listener();
                }
              });
      return () => subscription.unsubscribe();
    },
    [app, id],
  );
  const getSnapshot = useCallback(
    () =>
      app.runtime === 'legacy'
        ? app.conversationViewModel.state.overview
        : app.conversations.getOverview(id),
    [app, id],
  );
  const overview = useSyncExternalStore(subscribe, getSnapshot);
  return app.runtime !== 'legacy' && !conversation && loadResult === 'error'
    ? null
    : overview;
}

export function useThreadsAuthoritativeConversationTitle(): string | undefined {
  const overview = useThreadsCurrentConversationOverview();
  return overview?.title;
}

export function useThreadsCurrentConversationTitle(): string | undefined {
  const app = useThreadsApp();
  const {id, conversation} = useThreadsConversationViewModelState();
  const authoritativeTitle = useThreadsAuthoritativeConversationTitle();
  const isClientBusy = useConversationClientPending(conversation);
  const [presentation, setPresentation] = useState({
    conversationId: id,
    title: authoritativeTitle,
  });

  const displayedTitle =
    presentation.conversationId === id
      ? presentation.title
      : authoritativeTitle;

  useEffect(() => {
    if (presentation.conversationId !== id || !isClientBusy) {
      setPresentation({conversationId: id, title: authoritativeTitle});
    }
  }, [authoritativeTitle, id, isClientBusy, presentation.conversationId]);

  return app.runtime === 'legacy' ? authoritativeTitle : displayedTitle;
}

export function useThreadsConversationViewModelIsEnded(): boolean {
  return isConversationOverviewEnded(useThreadsCurrentConversationOverview());
}

export function useThreadsHistoryViewModelState(): LegacyHistoryViewModelState {
  const viewModel = useThreadsHistoryViewModel();
  const subscribe = useCallback(
    (listener: () => void) => {
      const subscription = viewModel.stateObservable().subscribe(listener);
      return () => subscription.unsubscribe();
    },
    [viewModel],
  );

  const getSnapshot = useCallback(() => {
    return viewModel.state;
  }, [viewModel]);

  return useSyncExternalStore(subscribe, getSnapshot);
}
